import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { speak, chime } from "../lib/speech";

const INHALE = 4;
const EXHALE = 6;

export default function CalmPage() {
  const navigate = useNavigate();
  const [duration, setDuration] = useState(null); // seconds
  const [phase, setPhase] = useState("in"); // in | out
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const phaseRef = useRef(null);

  useEffect(() => {
    if (!duration || done) return;
    const t = setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        const cycle = next % (INHALE + EXHALE);
        setPhase(cycle < INHALE ? "in" : "out");
        if (next >= duration) {
          setDone(true);
          chime();
          speak("Well done. You did it.");
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [duration, done]);

  useEffect(() => () => clearInterval(phaseRef.current), []);

  const start = (secs) => {
    setDuration(secs);
    setElapsed(0);
    setDone(false);
    setPhase("in");
    speak("Let's breathe together. Breathe in…");
  };

  return (
    <div data-testid="calm-page" className="min-h-screen bg-[#0A192F] text-[#FDFBF7] flex flex-col">
      <div className="p-4">
        <button
          data-testid="calm-exit-button"
          onClick={() => navigate("/use")}
          aria-label="Exit calm mode"
          className="min-w-[56px] min-h-[56px] rounded-full border-2 border-white/25 grid place-items-center text-white/80"
        >
          <X size={28} strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-24">
        {!duration ? (
          <>
            <h1 className="text-3xl font-extrabold text-center">Let's take a calm moment</h1>
            <p className="mt-2 text-lg text-white/70 text-center">Follow the circle. Breathe with it.</p>
            <div className="mt-10 flex gap-4">
              <button data-testid="calm-1min" onClick={() => start(60)} className="min-h-[96px] px-10 rounded-full bg-[#38B2AC] text-white text-2xl font-extrabold">1 minute</button>
              <button data-testid="calm-3min" onClick={() => start(180)} className="min-h-[96px] px-10 rounded-full bg-[#9F7AEA] text-white text-2xl font-extrabold">3 minutes</button>
            </div>
          </>
        ) : done ? (
          <div data-testid="calm-done" className="text-center">
            <p className="text-4xl font-extrabold">Well done.</p>
            <p className="mt-2 text-xl text-white/70">You took a calm moment.</p>
            <button data-testid="calm-back-button" onClick={() => navigate("/use")} className="mt-8 min-h-[64px] px-10 rounded-full bg-[#38B2AC] text-white text-xl font-bold">Back</button>
          </div>
        ) : (
          <div className="text-center">
            <div
              data-testid="calm-orb"
              className="mx-auto w-56 h-56 rounded-full"
              style={{
                background: "radial-gradient(circle, #38B2AC 0%, #2C7A7B 70%)",
                transform: phase === "in" ? "scale(1.35)" : "scale(1)",
                transition: phase === "in" ? `transform ${INHALE}s ease-in-out` : `transform ${EXHALE}s ease-in-out`,
                boxShadow: "0 0 80px rgba(56,178,172,0.35)",
              }}
            />
            <p data-testid="calm-phase-text" className="mt-12 text-3xl font-extrabold">
              {phase === "in" ? "Breathe in…" : "Breathe out…"}
            </p>
            <p className="mt-3 text-lg text-white/60 tabular-nums">{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
